import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2 } from "@activecollab/components";
import PropTypes from "prop-types";
import UserLink from "~/components/UserLink/UserLink";
import { getFilteredUsersFromProjectMembersForTaskUserSelector } from "~/store/selectors/selector.users.jsx";
import { replaceAvatarSize } from "~/utils/utils.jsx";
import SelectContainer from "../SelectContainer/SelectContainer.js";

const ProjectUserSelectContainer = ({
  projectId,
  selectedId,
  fakeAssigneeName,
  onChange,
  canEdit,
  hiddenFromClients,
}) => {
  const { t } = useTranslation();

  const users = useSelector((state) =>
    getFilteredUsersFromProjectMembersForTaskUserSelector(state, projectId, hiddenFromClients)
  );

  const selectedIdName = useMemo(() => {
    if (selectedId > 0) {
      return (
        <UserLink
          id={selectedId}
          hideAvatar
          nameProps={{ color: "secondary" }}
          context="user-select-for-assignee"
        />
      );
    } else if (typeof fakeAssigneeName === "string" && fakeAssigneeName.length > 0) {
      return fakeAssigneeName;
    }
    return `${t("No Assignee")}...`;
  }, [t, selectedId, fakeAssigneeName]);

  const options = useMemo(() => {
    const result = users.map((user) => ({
      id: user.id,
      name: user.name,
      image: replaceAvatarSize(user.image, 24),
    }));

    if (fakeAssigneeName?.length) {
      result.push({ id: 0, name: fakeAssigneeName });
    }

    return result;
  }, [fakeAssigneeName, users]);

  return canEdit ? (
    <SelectContainer
      placeholder={t("Choose an assignee")}
      defaultValue={t("No Assignee...")}
      selected={selectedId}
      options={options}
      onChange={onChange}
      className="tw-w-full"
    />
  ) : (
    <Body2 color="secondary">{selectedIdName}</Body2>
  );
};

ProjectUserSelectContainer.propTypes = {
  canEdit: PropTypes.bool,
  hiddenFromClients: PropTypes.bool,
  projectId: PropTypes.number.isRequired,
  fakeAssigneeName: PropTypes.string,
  selectedId: PropTypes.number,
  onChange: PropTypes.func,
};

ProjectUserSelectContainer.defaultProps = {
  canEdit: false,
  hiddenFromClients: false,
  selectedId: 0,
  onChange: () => {},
  fakeAssigneeName: "",
};

export default ProjectUserSelectContainer;
